import { useState, useCallback } from 'react';

/**
 * Hook för hantering av estimat-formuläret
 * Hanterar formData state och uppdateringsfunktioner
 */
const useEstimatForm = (
  initialFormData,
  dynamicEstimatCategories,
  setHasUnsavedChanges,
  setHasUnsavedNotes
) => {
  const [formData, setFormData] = useState(initialFormData);

  /**
   * Uppdatera formulärdata (projektInfo, beställare2, etc.)
   */
  const updateFormData = useCallback((section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
    
    // Separera estimatinnehåll från anteckningar
    if (section === 'intervjuFrågor') {
      setHasUnsavedNotes(true);
    } else {
      setHasUnsavedChanges(true);
    }
  }, [setHasUnsavedChanges, setHasUnsavedNotes]);

  /**
   * Uppdatera estimat-data (välj/avvälj poster, ändra pris/kvantitet)
   */
  const updateEstimat = useCallback((categoryKey, itemName, field, value) => {
    setFormData(prev => {
      // Säkerställ att estimat och kategori finns
      if (!prev.estimat) prev.estimat = {};
      if (!prev.estimat[categoryKey]) prev.estimat[categoryKey] = {};
      
      const currentItem = prev.estimat[categoryKey][itemName];
      
      // Om item inte finns, hämta default-värden från dynamicEstimatCategories
      let baseItem = currentItem || {};
      
      if (!currentItem) {
        // Hitta korrekt item från dynamicEstimatCategories för att få price, unit etc.
        const category = dynamicEstimatCategories[categoryKey];
        let foundItem = null;
        
        if (category?.subcategories) {
          // För subcategories (byggoffert), sök i alla subcategories
          Object.values(category.subcategories).forEach(subcategory => {
            const item = subcategory.items.find(item => {
              const itemKey = itemName.includes('_') ? itemName.split('_').slice(1).join('_') : itemName;
              return item.name === itemKey;
            });
            if (item) foundItem = item;
          });
        } else if (category?.items) {
          // För vanliga kategorier
          foundItem = category.items.find(item => item.name === itemName);
        }
        
        if (foundItem) {
          baseItem = {
            selected: false,
            quantity: foundItem.defaultQuantity,
            unit: foundItem.unit,
            price: foundItem.price,
            label: foundItem.name
          };
        }
      }
      
      // Uppdatera med det nya värdet
      const updatedItem = {
        ...baseItem,
        [field]: value
      };
      
      // Om vi checkar i en checkbox och antalet är 0, sätt antalet till 1
      if (field === 'selected' && value === true && (baseItem.quantity === 0 || !baseItem.quantity)) {
        updatedItem.quantity = 1;

        // Om config har defaultSubItems, initiera dem vid första select
        const category = dynamicEstimatCategories[categoryKey];
        let configItem = null;

        if (category?.subcategories) {
          Object.values(category.subcategories).forEach(subcategory => {
            const item = subcategory.items.find(item => {
              const itemKey = itemName.includes('_') ? itemName.split('_').slice(1).join('_') : itemName;
              return item.name === itemKey;
            });
            if (item) configItem = item;
          });
        } else if (category?.items) {
          configItem = category.items.find(item => item.name === itemName);
        }

        // Initiera defaultSubItems om de finns och subItems inte redan är satta
        if (configItem?.defaultSubItems && !updatedItem.subItems) {
          updatedItem.subItems = configItem.defaultSubItems.map((sub, idx) => ({
            id: `sub_${Date.now().toString(36)}_${idx}`,
            label: sub.name,
            // Använd defaultQuantity om > 0, annars 1 för att subItems ska vara användbara direkt
            quantity: sub.defaultQuantity > 0 ? sub.defaultQuantity : 1,
            price: sub.price || 0,
            unit: sub.unit || 'st',
            selected: true
          }));
          updatedItem.useSubItemsTotal = configItem.useSubItemsTotal ?? true;
        }
      }

      // Om vi uncheckar en checkbox, sätt antalet till 0
      if (field === 'selected' && value === false) {
        updatedItem.quantity = 0;
      }
      
      return {
        ...prev,
        estimat: {
          ...prev.estimat,
          [categoryKey]: {
            ...prev.estimat[categoryKey],
            [itemName]: updatedItem
          }
        }
      };
    });
    setHasUnsavedChanges(true);
  }, [dynamicEstimatCategories, setHasUnsavedChanges]);

  /**
   * Lägg till en egen rad i vald sektion (och ev. underkategori)
   * Returnerar labelKey som kan användas för att sätta fokus
   */
  /**
   * Beräkna nästa sortOrder för en kategori (eller subcategory)
   */
  const getNextSortOrder = useCallback((categoryKey, subcategoryKey = null) => {
    const categoryItems = formData.estimat?.[categoryKey] || {};
    const prefix = subcategoryKey ? `${subcategoryKey}_` : '';
    let max = -1;
    Object.entries(categoryItems).forEach(([key, item]) => {
      if (subcategoryKey && !key.startsWith(prefix)) return;
      if (!subcategoryKey && key.includes('_') && !key.startsWith('custom_')) return;
      const order = item?.sortOrder ?? -1;
      if (order > max) max = order;
    });
    return max + 1;
  }, [formData]);

  const handleAddCustomItem = useCallback((categoryKey, subcategoryKey = null) => {
    const uniqueId = `custom_${Date.now().toString(36)}`;
    const itemKey = subcategoryKey ? `${subcategoryKey}_${uniqueId}` : uniqueId;
    const nextOrder = getNextSortOrder(categoryKey, subcategoryKey);
    setFormData(prev => {
      const next = { ...prev, estimat: { ...(prev.estimat || {}) } };
      if (!next.estimat[categoryKey]) next.estimat[categoryKey] = {};
      // Standardvärden för ny rad
      next.estimat[categoryKey][itemKey] = {
        selected: true,
        quantity: 1,
        unit: 'st',
        price: 0,
        label: 'Ny rad',
        isCustom: true,
        sortOrder: nextOrder
      };
      return next;
    });
    setHasUnsavedChanges(true);

    // Returnera labelKey så anroparen kan sätta fokus
    const labelKey = subcategoryKey
      ? `${categoryKey}_${subcategoryKey}_${itemKey}`
      : `${categoryKey}_${itemKey}`;
    return labelKey;
  }, [setHasUnsavedChanges, getNextSortOrder]);

  /**
   * Ta bort rad (främst egna custom-rader men fungerar generellt)
   */
  const handleRemoveItem = useCallback((categoryKey, itemKey) => {
    setFormData(prev => {
      const next = { ...prev, estimat: { ...(prev.estimat || {}) } };
      if (!next.estimat[categoryKey]) return prev;
      const categoryItems = { ...next.estimat[categoryKey] };
      delete categoryItems[itemKey];
      next.estimat[categoryKey] = categoryItems;
      return next;
    });
    setHasUnsavedChanges(true);
  }, [setHasUnsavedChanges]);

  // =========================================================================
  // SUBITEMS-FUNKTIONER
  // =========================================================================

  /**
   * Lägg till en ny underrad till ett item
   */
  const addSubItem = useCallback((categoryKey, itemName) => {
    const subItemId = `sub_${Date.now().toString(36)}`;
    setFormData(prev => {
      const next = { ...prev, estimat: { ...(prev.estimat || {}) } };
      if (!next.estimat[categoryKey]) return prev;

      const item = next.estimat[categoryKey][itemName];
      if (!item) return prev;

      // Initiera subItems array om den inte finns
      const subItems = item.subItems ? [...item.subItems] : [];

      // Om detta är första underraden, sätt useSubItemsTotal till true
      const isFirstSubItem = subItems.length === 0;

      // Lägg till ny underrad
      subItems.push({
        id: subItemId,
        label: 'Ny underrad',
        quantity: 1,
        price: 0,
        unit: 'st',
        selected: true
      });

      next.estimat[categoryKey] = {
        ...next.estimat[categoryKey],
        [itemName]: {
          ...item,
          subItems,
          // Sätt useSubItemsTotal till true när första underraden läggs till
          useSubItemsTotal: isFirstSubItem ? true : item.useSubItemsTotal
        }
      };

      return next;
    });
    setHasUnsavedChanges(true);
    return subItemId;
  }, [setHasUnsavedChanges]);

  /**
   * Uppdatera en underrad
   */
  const updateSubItem = useCallback((categoryKey, itemName, subItemId, field, value) => {
    setFormData(prev => {
      const next = { ...prev, estimat: { ...(prev.estimat || {}) } };
      if (!next.estimat[categoryKey]) return prev;

      const item = next.estimat[categoryKey][itemName];
      if (!item || !item.subItems) return prev;

      const subItems = item.subItems.map(sub => {
        if (sub.id === subItemId) {
          const updated = { ...sub, [field]: value };

          // Auto-select om quantity > 0
          if (field === 'quantity' && value > 0 && !sub.selected) {
            updated.selected = true;
          }
          // Auto-deselect om quantity = 0
          if (field === 'quantity' && value === 0) {
            updated.selected = false;
          }

          return updated;
        }
        return sub;
      });

      next.estimat[categoryKey] = {
        ...next.estimat[categoryKey],
        [itemName]: {
          ...item,
          subItems
        }
      };

      return next;
    });
    setHasUnsavedChanges(true);
  }, [setHasUnsavedChanges]);

  /**
   * Ta bort en underrad
   */
  const removeSubItem = useCallback((categoryKey, itemName, subItemId) => {
    setFormData(prev => {
      const next = { ...prev, estimat: { ...(prev.estimat || {}) } };
      if (!next.estimat[categoryKey]) return prev;

      const item = next.estimat[categoryKey][itemName];
      if (!item || !item.subItems) return prev;

      const subItems = item.subItems.filter(sub => sub.id !== subItemId);

      next.estimat[categoryKey] = {
        ...next.estimat[categoryKey],
        [itemName]: {
          ...item,
          subItems
        }
      };

      return next;
    });
    setHasUnsavedChanges(true);
  }, [setHasUnsavedChanges]);

  /**
   * Växla useSubItemsTotal för ett item
   * true = endast subItems-summa, false = eget pris + subItems
   */
  const toggleUseSubItemsTotal = useCallback((categoryKey, itemName) => {
    setFormData(prev => {
      const next = { ...prev, estimat: { ...(prev.estimat || {}) } };
      if (!next.estimat[categoryKey]) return prev;

      const item = next.estimat[categoryKey][itemName];
      if (!item) return prev;

      next.estimat[categoryKey] = {
        ...next.estimat[categoryKey],
        [itemName]: {
          ...item,
          useSubItemsTotal: !item.useSubItemsTotal
        }
      };

      return next;
    });
    setHasUnsavedChanges(true);
  }, [setHasUnsavedChanges]);

  /**
   * Flytta en rad inom en kategori/subcategory (drag & drop)
   * @param {string} categoryKey - Kategorinyckeln
   * @param {string|null} subcategoryKey - Subcategory eller null för flat
   * @param {Array} sortedKeys - Den sorterade listan av item-nycklar
   * @param {number} sourceIndex - Varifrån
   * @param {number} destIndex - Vart
   */
  const handleReorderItems = useCallback((categoryKey, subcategoryKey, sortedKeys, sourceIndex, destIndex) => {
    if (sourceIndex === destIndex) return;
    const reordered = [...sortedKeys];
    const [moved] = reordered.splice(sourceIndex, 1);
    reordered.splice(destIndex, 0, moved);

    setFormData(prev => {
      const next = { ...prev, estimat: { ...(prev.estimat || {}) } };
      if (!next.estimat[categoryKey]) return prev;
      const categoryItems = { ...next.estimat[categoryKey] };

      reordered.forEach((key, idx) => {
        if (categoryItems[key]) {
          categoryItems[key] = { ...categoryItems[key], sortOrder: idx };
        }
      });

      next.estimat[categoryKey] = categoryItems;
      return next;
    });
    setHasUnsavedChanges(true);
  }, [setHasUnsavedChanges]);

  return {
    formData,
    setFormData,
    updateFormData,
    updateEstimat,
    handleAddCustomItem,
    handleRemoveItem,
    handleReorderItems,
    // SubItems-funktioner
    addSubItem,
    updateSubItem,
    removeSubItem,
    toggleUseSubItemsTotal,
  };
};

export default useEstimatForm;
